import React, { useEffect, useState, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeContext } from '../../contexts/ThemeContext';

interface AuthBackgroundProps {
    children: ReactNode;
    marqueeText?: string;
    maxWidth?: number | string;
}

const AuthBackground: React.FC<AuthBackgroundProps> = ({
    children,
    marqueeText = "YOUR RIDE // YOUR RULES // NO SURGES // JUST VIBES //",
    maxWidth = 480
}) => {
    const { theme } = useThemeContext();
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const updateMousePosition = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };

        window.addEventListener('mousemove', updateMousePosition);

        return () => {
            window.removeEventListener('mousemove', updateMousePosition);
        };
    }, []);

    // Determine if we should show the dark mode effects based on the actual theme
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100vh',
                width: '100%',
                bgcolor: theme.palette.background.default,
                color: theme.palette.text.primary,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                overflow: 'hidden',
                padding: 3,
                paddingTop: '2vh', // Pulled up close to navbar
            }}
        >
            {/* 1. Ambient Glass / Glowing Orbs (Only visible/prominent in dark mode) */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: isDark ? 0.85 : 0.3, // Significantly increased opacity for visibility
                    pointerEvents: 'none',
                    zIndex: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Primary Color Orb (Mouse Tracker) */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '600px',
                        height: '600px',
                        background: `radial-gradient(circle, ${theme.palette.primary.main}70 0%, transparent 80%)`, // Increased intensity
                        borderRadius: '50%',
                        top: mousePosition.y - 300,
                        left: mousePosition.x - 300,
                        transition: 'top 0.8s ease-out, left 0.8s ease-out',
                        filter: 'blur(40px)', // Reduced blur for sharper visibility
                    }}
                />

                {/* Secondary Color Orb (Static / Slow pulsing) */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '800px',
                        height: '800px',
                        background: `radial-gradient(circle, ${theme.palette.secondary.main}50 0%, transparent 80%)`, // Increased intensity
                        borderRadius: '50%',
                        top: '-20%',
                        right: '-10%',
                        filter: 'blur(50px)', // Reduced blur for sharper visibility
                        animation: 'pulse 8s infinite alternate ease-in-out',
                        '@keyframes pulse': {
                            '0%': { transform: 'scale(1) translate(0, 0)' },
                            '100%': { transform: 'scale(1.1) translate(-50px, 50px)' },
                        },
                    }}
                />
            </Box>

            {/* 2. Brutalist Typographic Marquee */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-10deg)',
                    width: '200vw',
                    pointerEvents: 'none',
                    zIndex: 1,
                    opacity: isDark ? 0.15 : 0.08, // Increased opacity for marquee text visibility
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4rem',
                }}
            >
                {[...Array(3)].map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            whiteSpace: 'nowrap',
                            animation: `marquee ${i % 2 === 0 ? '40s' : '50s'} linear infinite ${i % 2 !== 0 ? 'reverse' : 'normal'}`,
                            '@keyframes marquee': {
                                '0%': { transform: 'translateX(0)' },
                                '100%': { transform: 'translateX(-50%)' },
                            },
                        }}
                    >
                        {[...Array(4)].map((_, j) => (
                            <Typography
                                key={j}
                                variant="h1"
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: { xs: '6rem', md: '10rem', lg: '14rem' },
                                    fontWeight: 900,
                                    WebkitTextStroke: `2px ${theme.palette.text.primary}`,
                                    WebkitTextFillColor: 'transparent',
                                    paddingRight: '2rem',
                                }}
                            >
                                {marqueeText}
                            </Typography>
                        ))}
                    </Box>
                ))}
            </Box>

            {/* 3. The Auth Card passed as children */}
            <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth }}>
                {children}
            </Box>
        </Box>
    );
};

export default AuthBackground;
